import { useEffect, useState } from "react";
import {
    FiSearch,
    FiLoader,
    FiActivity,
    FiUsers,
    FiZap,
    FiMinus,
    FiPlus,
} from "react-icons/fi";
import { useDebouncedCallback } from "use-debounce";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchCreditUsageHistory } from "../../store/smileDesign/paymentSlice";
import TablePagination from "../../components/smileDesign/TablePagination";
import { CREDIT_SERVICE } from "../../constants/creditService";

const formatDate = (value: string) => {
    const date = new Date(value);
    return date.toLocaleString();
};

const typeLabels: Record<string, { label: string; className: string }> = {
    USAGE: {
        label: "Used",
        className: "bg-red-500/10 border-red-500/20 text-red-400",
    },
    GENERATION: {
        label: "Admin generation",
        className: "bg-cyan-500/10 border-cyan-500/20 text-cyan-400",
    },
    PURCHASE: {
        label: "Purchase",
        className: "bg-green-500/10 border-green-500/20 text-green-400",
    },
    MANUAL_ADD: {
        label: "Manual add",
        className: "bg-green-500/10 border-green-500/20 text-green-400",
    },
    MANUAL_REMOVE: {
        label: "Manual remove",
        className: "bg-orange-500/10 border-orange-500/20 text-orange-400",
    },
    REFUND: {
        label: "Refund",
        className: "bg-purple-500/10 border-purple-500/20 text-purple-400",
    },
    STARTER: {
        label: "Starter",
        className: "bg-yellow-500/10 border-yellow-500/20 text-yellow-400",
    },
};

const serviceLabels: Record<string, string> = {
    [CREDIT_SERVICE.SMILE]: "Smile Design",
    [CREDIT_SERVICE.PHOTO_RECOLOUR]: "Photo Recolour",
};

const CreditUsageHistory = () => {
    const dispatch = useAppDispatch();
    const {
        creditUsage,
        creditUsagePagination,
        creditUsageStats,
        creditUsageLoading,
        error,
    } = useAppSelector((state) => state.payment);

    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("USAGE");
    const [serviceFilter, setServiceFilter] = useState<string>(
        CREDIT_SERVICE.SMILE
    );
    const [page, setPage] = useState(1);
    const limit = 20;

    const loadHistory = (overrides?: {
        page?: number;
        search?: string;
        type?: string;
        service?: string;
    }) => {
        dispatch(
            fetchCreditUsageHistory({
                page: overrides?.page ?? page,
                limit,
                search: (overrides?.search ?? search) || undefined,
                type: (overrides?.type ?? typeFilter) || undefined,
                service: overrides?.service ?? serviceFilter,
            })
        );
    };

    const debouncedSearch = useDebouncedCallback((value: string) => {
        setPage(1);
        loadHistory({ page: 1, search: value });
    }, 400);

    useEffect(() => {
        loadHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearchChange = (value: string) => {
        setSearch(value);
        debouncedSearch(value);
    };

    const handleTypeFilter = (value: string) => {
        setTypeFilter(value);
        setPage(1);
        loadHistory({ page: 1, type: value });
    };

    const handleServiceFilter = (value: string) => {
        setServiceFilter(value);
        setPage(1);
        loadHistory({ page: 1, service: value });
    };

    return (
        <div className="min-h-screen bg-[#080808] text-white p-4 sm:p-6">
            <div className="w-full">
                <div className="mb-8">
                    <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                        Credit Usage History
                    </h1>
                    <p className="text-[#8b8f8d] mt-2 text-sm sm:text-base">
                        See which client used credits, when they used them, and
                        where they were spent.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                    <div className="bg-[#101211] border border-[#1f2221] rounded-2xl p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[#8b8f8d] text-sm">Events</p>
                                <h2 className="text-3xl font-bold text-white mt-2">
                                    {creditUsageStats?.totalEvents ?? 0}
                                </h2>
                            </div>
                            <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400">
                                <FiActivity className="text-xl" />
                            </div>
                        </div>
                    </div>
                    <div className="bg-[#101211] border border-[#1f2221] rounded-2xl p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[#8b8f8d] text-sm">
                                    Credits used
                                </p>
                                <h2 className="text-3xl font-bold text-red-400 mt-2">
                                    {creditUsageStats?.creditsUsed ?? 0}
                                </h2>
                            </div>
                            <div className="w-12 h-12 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400">
                                <FiZap className="text-xl" />
                            </div>
                        </div>
                    </div>
                    <div className="bg-[#101211] border border-[#1f2221] rounded-2xl p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[#8b8f8d] text-sm">Clients</p>
                                <h2 className="text-3xl font-bold text-white mt-2">
                                    {creditUsageStats?.uniqueClients ?? 0}
                                </h2>
                            </div>
                            <div className="w-12 h-12 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                                <FiUsers className="text-xl" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-4 mb-6">
                    <div className="relative flex-1">
                        <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b6f6d]" />
                        <input
                            type="text"
                            placeholder="Search client, email, token, or where credits were used..."
                            value={search}
                            onChange={(e) =>
                                handleSearchChange(e.target.value)
                            }
                            className="w-full bg-[#101211] border border-[#1f2221] rounded-2xl py-3 pl-12 pr-4 text-white placeholder:text-[#6b6f6d] focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                    </div>

                    <select
                        value={typeFilter}
                        onChange={(e) => handleTypeFilter(e.target.value)}
                        className="bg-[#101211] border border-[#1f2221] rounded-2xl py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 lg:min-w-[180px]"
                    >
                        <option value="">All types</option>
                        <option value="USAGE">Used</option>
                        <option value="PURCHASE">Purchase</option>
                        <option value="GENERATION">Admin generation</option>
                        <option value="MANUAL_ADD">Manual add</option>
                        <option value="MANUAL_REMOVE">Manual remove</option>
                        <option value="REFUND">Refund</option>
                        <option value="STARTER">Starter</option>
                    </select>

                    <select
                        value={serviceFilter}
                        onChange={(e) => handleServiceFilter(e.target.value)}
                        className="bg-[#101211] border border-[#1f2221] rounded-2xl py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 lg:min-w-[180px]"
                    >
                        <option value={CREDIT_SERVICE.SMILE}>
                            Smile Design
                        </option>
                        <option value={CREDIT_SERVICE.PHOTO_RECOLOUR}>
                            Photo Recolour
                        </option>
                        <option value="all">All services</option>
                    </select>
                </div>

                {error && (
                    <div className="mb-6 rounded-2xl border border-red-500/20 bg-red-500/10 text-red-400 px-5 py-4">
                        {error}
                    </div>
                )}

                {creditUsageLoading && (
                    <div className="flex justify-center py-20">
                        <FiLoader className="animate-spin text-cyan-400 text-4xl" />
                    </div>
                )}

                {!creditUsageLoading && creditUsage.length === 0 && (
                    <div className="text-center py-20 rounded-3xl border border-[#1f2221] bg-[#101211]">
                        <FiActivity className="text-5xl text-[#3a3d3c] mx-auto mb-4" />
                        <h3 className="text-xl font-semibold">
                            No credit usage found
                        </h3>
                        <p className="text-[#8b8f8d] mt-2">
                            Client credit activity will appear here.
                        </p>
                    </div>
                )}

                {!creditUsageLoading && creditUsage.length > 0 && (
                    <>
                        <div className="hidden lg:block bg-[#101211] border border-[#1f2221] rounded-3xl overflow-hidden shadow-2xl">
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[1080px]">
                                    <thead className="bg-[#080808] border-b border-[#1f2221]">
                                        <tr>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                                When
                                            </th>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                                Client
                                            </th>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                                Where
                                            </th>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                                Type
                                            </th>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                                Credits
                                            </th>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                                Balance
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {creditUsage.map((entry) => {
                                            const type =
                                                typeLabels[
                                                    entry.transaction_type
                                                ] ?? {
                                                    label: entry.transaction_type,
                                                    className:
                                                        "bg-[#1f2221] text-[#b7bab8]",
                                                };
                                            return (
                                                <tr
                                                    key={entry.ledger_id}
                                                    className="border-b border-[#1f2221] hover:bg-[#151716] transition-all"
                                                >
                                                    <td className="px-6 py-5 text-sm text-[#b7bab8] whitespace-nowrap">
                                                        {formatDate(
                                                            entry.created_at
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-5">
                                                        <div className="font-medium">
                                                            {entry.user_name}
                                                        </div>
                                                        <div className="text-xs text-[#8b8f8d]">
                                                            {entry.email}
                                                        </div>
                                                        {entry.token && (
                                                            <div className="text-xs text-[#6b6f6d] mt-0.5">
                                                                {entry.token}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-5">
                                                        <p className="text-white">
                                                            {entry.used_for}
                                                        </p>
                                                        <p className="text-xs text-[#8b8f8d] mt-1">
                                                            {serviceLabels[
                                                                entry.service
                                                            ] ?? entry.service}
                                                        </p>
                                                    </td>
                                                    <td className="px-6 py-5">
                                                        <span
                                                            className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${type.className}`}
                                                        >
                                                            {type.label}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-5 font-semibold">
                                                        <span
                                                            className={`inline-flex items-center gap-1.5 ${
                                                                entry.is_debit
                                                                    ? "text-red-400"
                                                                    : "text-green-400"
                                                            }`}
                                                        >
                                                            {entry.is_debit ? (
                                                                <FiMinus />
                                                            ) : (
                                                                <FiPlus />
                                                            )}
                                                            {entry.credits_abs}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-5 text-sm text-[#b7bab8] whitespace-nowrap">
                                                        {entry.balance_before}{" "}
                                                        →{" "}
                                                        <span className="text-white font-medium">
                                                            {
                                                                entry.balance_after
                                                            }
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <TablePagination
                                pagination={creditUsagePagination}
                                onPageChange={(nextPage) => {
                                    setPage(nextPage);
                                    loadHistory({ page: nextPage });
                                }}
                                itemLabel="events"
                            />
                        </div>

                        <div className="lg:hidden space-y-4">
                            {creditUsage.map((entry) => {
                                const type =
                                    typeLabels[entry.transaction_type] ?? {
                                        label: entry.transaction_type,
                                        className:
                                            "bg-[#1f2221] text-[#b7bab8]",
                                    };
                                return (
                                    <div
                                        key={entry.ledger_id}
                                        className="bg-[#101211] border border-[#1f2221] rounded-2xl p-4"
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <span
                                                className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border ${type.className}`}
                                            >
                                                {type.label}
                                            </span>
                                            <p className="text-xs text-[#8b8f8d] text-right shrink-0">
                                                {formatDate(entry.created_at)}
                                            </p>
                                        </div>
                                        <p
                                            className={`mt-3 text-lg font-semibold ${
                                                entry.is_debit
                                                    ? "text-red-400"
                                                    : "text-green-400"
                                            }`}
                                        >
                                            {entry.is_debit ? "-" : "+"}
                                            {entry.credits_abs} credits
                                        </p>
                                        <div className="mt-3 space-y-2 text-sm">
                                            <div>
                                                <p className="text-[#6b6f6d] text-xs">
                                                    Client
                                                </p>
                                                <p className="font-medium break-words">
                                                    {entry.user_name}
                                                </p>
                                                <p className="text-xs text-[#8b8f8d] break-all">
                                                    {entry.email}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-[#6b6f6d] text-xs">
                                                    Where
                                                </p>
                                                <p className="text-white break-words">
                                                    {entry.used_for}
                                                </p>
                                                <p className="text-xs text-[#8b8f8d]">
                                                    {serviceLabels[
                                                        entry.service
                                                    ] ?? entry.service}
                                                </p>
                                            </div>
                                            <p className="text-[#b7bab8]">
                                                Balance: {entry.balance_before}{" "}
                                                →{" "}
                                                <span className="text-white font-medium">
                                                    {entry.balance_after}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                            <div className="bg-[#101211] border border-[#1f2221] rounded-2xl overflow-hidden">
                                <TablePagination
                                    pagination={creditUsagePagination}
                                    onPageChange={(nextPage) => {
                                        setPage(nextPage);
                                        loadHistory({ page: nextPage });
                                    }}
                                    itemLabel="events"
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CreditUsageHistory;
